/* eslint-disable max-lines-per-function */
import dayjs from 'dayjs';
import { useCalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { DayCellHeader } from '@/domain/termine/features/Calendar/Grid/DayCell/DayCellHeader';
import { cn } from '@/lib/utils';
import { DayCellTabletEntries } from '@/domain/termine/features/Calendar/Grid/DayCell/DayCellTabletEntries';
import { DayCellDesktopEntries } from '@/domain/termine/features/Calendar/Grid/DayCell/DayCellDesktopEntries';
import { DayCellMobileEntries } from '@/domain/termine/features/Calendar/List/DayCell/DayCellMobileEntries';
import { Button } from '@/components/ui/button';
import { faPlusLarge } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams, useNavigate } from 'react-router-dom';
import { YearMonthParams } from '@/domain/termine/type';
import { Termin } from '@/domain/termine/shared/types/Termin';

interface DayCellProps {
  day: dayjs.Dayjs;
}

function splitTermine(events: Termin[], visibleCount: number) {
  return {
    visible: events.slice(0, visibleCount),
    hidden: events.slice(visibleCount),
  };
}

export function DayCell({ day }: DayCellProps) {
  const { termineByDay } = useCalendarContext();
  const { year, month } = useParams<YearMonthParams>();
  const navigate = useNavigate();
  const termineForDay = termineByDay.get(day.format('YYYY-MM-DD')) ?? [];
  const desktopTermine = splitTermine(termineForDay, 2);
  const tabletTermine = splitTermine(termineForDay, 1);

  const createFormUrl = `/termine/uebersicht/${year}/${month}/create/${day.format(
    'YYYY-MM-DD',
  )}`;

  const handleCellClick = (e: React.MouseEvent) => {
    if (window.innerWidth >= 640 && e.target === e.currentTarget) {
      navigate(createFormUrl);
    }
  };

  return (
    <div
      className={cn(
        'text-end p-0 md:p-1 text-sm',
        'text-neutrals-N900 border-neutrals-N300',
        'border-r last:border-r-0 md:min-h-40',
        'group sm:cursor-pointer',
      )}
      onClick={handleCellClick}>
      <div className="flex flex-col h-full relative">
        {termineForDay.length === 0 && (
          <div className="absolute inset-0 flex justify-center items-center pointer-events-none">
            <Button
              variant="default"
              className={cn(
                'h-10 w-10 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-auto',
              )}
              size="icon"
              aria-label="Termin anlegen"
              tabIndex={-1}
              onClick={(e) => {
                e.stopPropagation();
                navigate(createFormUrl);
              }}>
              <FontAwesomeIcon icon={faPlusLarge} />
            </Button>
          </div>
        )}
        {/* Mobile (sm only) */}
        <div className="flex flex-col sm:hidden">
          <DayCellMobileEntries termine={termineForDay} day={day} />
        </div>
        {/* Tablet (md only) */}
        <div className="flex-col hidden sm:block">
          <DayCellHeader day={day} termine={termineForDay} />
          <DayCellTabletEntries
            visible={tabletTermine.visible}
            hidden={tabletTermine.hidden}
            day={day}
          />
          {/* Desktop (lg and above) */}
          <DayCellDesktopEntries
            visible={desktopTermine.visible}
            hidden={desktopTermine.hidden}
            day={day}
          />
        </div>
      </div>
    </div>
  );
}
